import { Injectable, inject } from '@angular/core';
import { Apollo } from 'apollo-angular';
import {
  DocumentNode,
  FetchPolicy,
  WatchQueryFetchPolicy,
} from '@apollo/client/core';
import {
  catchError,
  delay,
  delayWhen,
  of,
  retry,
  throwError,
  timeout,
} from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class GraphqlHostService {
  apollo = inject(Apollo);

  /**
   * Executes a GraphQL query using the specified server, query, variables, and fetch policy.
   * @param {string} server - The server to execute the query on.
   * @param {DocumentNode} query - The GraphQL query to execute.
   * @param {Object} vars - The variables to pass to the query.
   * @param {FetchPolicy} policy - The fetch policy to use for the query. Defaults to 'network-only'.
   * @param {number} retryAttempts - The number of times to retry the query if it fails. Defaults to 3.
   * @param {number} timeOut - The time in milliseconds to wait for the query to complete before timing out. Defaults to 30000.
   * @returns {Observable<FetchResult<any>>} - An Observable that emits the result of the query.
   */
  query<T = unknown>(
    server: string,
    query: DocumentNode,
    vars?: object,
    policy: FetchPolicy = 'network-only',
    retryAttempts = 3,
    timeOut = 30000,
  ) {
    return this.apollo
      .use(server)
      .query<T>({
        query: query,
        variables: vars,
        fetchPolicy: policy,
      })
      .pipe(
        timeout(timeOut),
        retry({
          count: retryAttempts,
          delay: (error) =>
            of(error).pipe(delayWhen(() => of(null).pipe(delay(1000)))),
        }),
        catchError((error) => {
          console.error('An error occurred on : query', error);
          return throwError(() => error);
        }),
      );
  }

  /**
   * Executes a mutation on the specified server using Apollo Client.
   * @param {string} server - The server to use for the mutation.
   * @param {DocumentNode} mutate - The mutation to execute.
   * @param {Object} vars - The variables to pass to the mutation.
   * @param {number} retryAttempts - The number of times to retry the mutation if it fails. Defaults to 1.
   * @returns {Observable<any>} - An observable that emits the result of the mutation.
   */
  mutate<T = unknown>(
    server: string,
    mutate: DocumentNode,
    vars: object,
    retryAttempts = 1,
    timeOut = 60000,
  ) {
    return this.apollo
      .use(server)
      .mutate<T>({
        mutation: mutate,
        variables: vars,
      })
      .pipe(
        timeout(timeOut),
        retry({
          count: retryAttempts,
          delay: (error) => {
            return of(error).pipe(delay(1000));
          },
        }),
        catchError((error) => {
          console.error('An error occurred on : mutate', error);
          return throwError(() => error);
        }),
      );
  }

  /**
   * @param serverName
   * @param graphQuery
   * @param vars
   * @param policy
   * @returns The graphQl query data pulled from the GraphQL API.
   */
  watchQuery(
    serverName: string,
    graphQuery: DocumentNode,
    vars?: object,
    policy: WatchQueryFetchPolicy = 'network-only',
  ) {
    return this.apollo.use(serverName).watchQuery({
      query: graphQuery,
      variables: vars,
      fetchPolicy: policy,
    });
  }
}
